import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { Controller, useForm } from "react-hook-form";
import {
  ErrorState,
  Button,
  Checkbox,
  FormField,
  IconButton,
  PhoneInputField,
  TextInput,
} from "@a2z/ui";
import { keys } from "@/lib/i18n/portal";
import { resolvePublicSellerRedirectPath } from "@/lib/auth/public-seller-auth";
import { toE164OrNull } from "@/lib/phone/phone";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { apiErrorMessage } from "@/lib/api/error-copy";
import { useLoginMutation } from "@/modules/auth/services/useLoginMutation";
import { loginForbiddenMessageKey } from "@/modules/auth/lib/login-refusal";
import { apiFetchClient } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type { SellerAuthSessionResponse } from "@/lib/api/types";
import {
  LOGIN_NOTICE_NO_STORE,
  LOGIN_NOTICE_STORE_DEACTIVATED,
} from "@/lib/auth/session-notice";
import { useLogoutMutation } from "@/shared/services/useLogoutMutation";
import { markSignedInBefore } from "@/modules/auth/lib/first-visit";
import { safeAuthReturnPath, withAuthReturnPath } from "@/lib/auth/safe-return";
import { getPrivateSessionState } from "@/lib/api/private-session";
import { waitForLoginSession } from "@/modules/auth/lib/wait-for-login-session";

type SellerEntryValues = {
  phone: string;
  password: string;
  remember: boolean;
};

export function StoreLookupForm({
  locale,
  showRememberMe = true,
}: {
  locale: string;
  showRememberMe?: boolean;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = useTranslations();
  // `?notice=no-store`: a member removed while signed in was sent here — say
  // why with the login's own sentence (B10). `?notice=password-saved`: the
  // first-login password was saved on a backend that ended the session.
  const notice = searchParams.get("notice");
  const [globalError, setGlobalError] = useState<string | null>(() =>
    notice === LOGIN_NOTICE_NO_STORE
      ? t(keys.errors.sellerNoStore)
      : notice === LOGIN_NOTICE_STORE_DEACTIVATED
        ? t(keys.errors.storeDeactivated)
        : null,
  );
  // An admin deactivated the store mid-session: the cookie is still sound but
  // every store call is 403, so end it here (once) instead of leaving a
  // session that bounces between the app and this page.
  const { mutateAsync: signOut } = useLogoutMutation();
  const signedOutRef = useRef(false);
  useEffect(() => {
    if (notice !== LOGIN_NOTICE_STORE_DEACTIVATED || signedOutRef.current) return;
    signedOutRef.current = true;
    void signOut({}).catch(() => undefined);
  }, [notice, signOut]);
  const passwordSavedNotice = notice === "password-saved";

  // Post-login return target (e.g. an invite page that bounced here with 401).
  const safeNext =
    searchParams.getAll("next").length === 1
      ? safeAuthReturnPath(searchParams.get("next"))
      : null;
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    mutateAsync: loginSeller,
    loginFailure,
    isPending,
    reset: clearLoginFailure,
  } = useLoginMutation();
  const {
    control,
    handleSubmit,
    clearErrors,
    setError,
    register,
    formState: { errors, isSubmitting },
  } = useForm<SellerEntryValues>({
    defaultValues: {
      phone: "",
      password: "",
      remember: true,
    },
  });
  const passwordToggleAriaLabel = isPasswordVisible
    ? `${t(keys.common.hide)} ${t(keys.common.password)}`
    : `${t(keys.common.show)} ${t(keys.common.password)}`;

  const handleStoreSelected = (redirectPath: string) => {
    navigate(resolvePublicSellerRedirectPath(locale, redirectPath), {
      replace: true,
    });
  };

  // The login response does NOT include `availableStores`, so after a successful
  // login we fetch the session once (modeled on RootRedirectPage) to decide
  // whether to show the store picker. Multi-store sellers (>1) → `/select-store`;
  // everyone else keeps the EXACT existing flow (resolve `redirectPath` and
  // navigate straight to the store). Any failure to determine store count falls
  // back to the existing single-store behavior, so single-store login is never
  // blocked or given an extra hop.
  const routeAfterLogin = async (redirectPath: string, generation: number) => {
    const current = () =>
      !getPrivateSessionState().changing &&
      getPrivateSessionState().generation === generation;
    if (!current()) return;
    // A `?next=` return target (invite acceptance, etc.) wins over the default
    // store routing AND the multi-store picker hop, so the user lands back
    // exactly where they were sent to log in.
    if (safeNext) {
      navigate(safeNext, { replace: true });
      return;
    }
    try {
      const response = await apiFetchClient(endpoints.seller.auth.me, {
        cache: "no-store",
      });
      if (response.ok) {
        const session = (await response.json()) as SellerAuthSessionResponse;
        if (!current()) return;
        if ((session.availableStores?.length ?? 0) > 1) {
          navigate(`/${locale}/select-store`, { replace: true });
          return;
        }
      }
    } catch {
      // Fall through to the existing behavior below.
    }
    if (current()) handleStoreSelected(redirectPath);
  };

  const onSubmit = async ({ phone, password }: SellerEntryValues) => {
    setGlobalError(null);
    clearErrors();
    const normalizedPhone = toE164OrNull(phone);
    if (!normalizedPhone) {
      setError("phone", {
        type: "manual",
        message: t(keys.buyer.trackingValidationPhone),
      });
      return;
    }

    const expectedGeneration = getPrivateSessionState().generation + 1;
    try {
      const payload = await loginSeller({
        phone: normalizedPhone,
        password,
      });
      await waitForLoginSession(expectedGeneration);
      if (
        getPrivateSessionState().changing ||
        getPrivateSessionState().generation !== expectedGeneration
      )
        return;
      if (payload.mustChangePassword) {
        navigate(withAuthReturnPath(`/${locale}/change-password`, safeNext), {
          replace: true,
        });
        return;
      }
      markSignedInBefore();
      await routeAfterLogin(payload.redirectPath, expectedGeneration);
    } catch (caughtError) {
      if (!(caughtError instanceof Error)) {
        setGlobalError(t(keys.errors.connectionIssue));
        return;
      }
      if (
        caughtError instanceof ApiJsonClientError &&
        caughtError.status === 401
      ) {
        const invalidCredentialsMessage = t(
          keys.errors.invalidSellerCredentials,
        );
        setError("phone", {
          type: "server",
          message: invalidCredentialsMessage,
        });
        setError("password", {
          type: "server",
          message: invalidCredentialsMessage,
        });
        return;
      }

      if (
        caughtError instanceof ApiJsonClientError &&
        caughtError.status === 403
      ) {
        setGlobalError(t(loginForbiddenMessageKey(caughtError.code)));
        return;
      }

      setGlobalError(apiErrorMessage(caughtError));
    }
  };

  const visibleGlobalError =
    globalError ||
    (loginFailure
      ? loginFailure.status === 401
        ? t(keys.errors.invalidSellerCredentials)
        : loginFailure.status === 403
          ? t(loginForbiddenMessageKey(loginFailure.code))
          : loginFailure.message || t(keys.errors.connectionIssue)
      : null);

  const passwordLabel = (
    <span className="flex items-baseline justify-between gap-3">
      <span>{t(keys.common.password)}</span>
      <Link
        to={`/${locale}/forgot-password`}
        className="text-[11.5px] font-medium text-[color:var(--accent-600)] underline-offset-4 hover:underline"
      >
        {t(keys.auth.forgotPassword)}
      </Link>
    </span>
  );

  return (
    <form
      className="flex w-full flex-col gap-4"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      {visibleGlobalError ? (
        <ErrorState message={visibleGlobalError} />
      ) : passwordSavedNotice ? (
        <p
          role="status"
          className="rounded-[12px] bg-[color:var(--success-100)] px-3 py-2.5 text-[13.5px] font-semibold text-[color:var(--success-text)]"
        >
          {t(keys.auth.passwordSavedSignIn)}
        </p>
      ) : null}

      <FormField
        label={t(keys.common.phone)}
        name="phone"
        error={errors.phone?.message}
      >
        <Controller
          control={control}
          name="phone"
          rules={{
            required: t(keys.errors.required),
          }}
          render={({ field }) => (
            <PhoneInputField
              name={field.name}
              value={field.value ?? ""}
              onChange={(value) => {
                field.onChange(value);
                setGlobalError(null);
                clearLoginFailure();
                clearErrors(["phone", "password"]);
              }}
              onBlur={field.onBlur}
              autoComplete="tel"
              placeholder={t(keys.placeholders.phone)}
              // Without `labels` the shared field falls back to its English
              // defaults ("e.g. {example}", "Country", "Search countries…"),
              // which leaked into the Arabic login/lookup screens.
              labels={{
                countryLabel: t(keys.forms.phoneCountryLabel),
                countryPlaceholder: t(keys.forms.phoneCountryPlaceholder),
                countrySearchPlaceholder: t(
                  keys.forms.phoneCountrySearchPlaceholder,
                ),
                countryNoResults: t(keys.forms.phoneCountryNoResults),
                phonePlaceholder: t(keys.forms.phoneNationalPlaceholder, {
                  example: "{example}",
                }),
              }}
              hasError={Boolean(errors.phone)}
            />
          )}
        />
      </FormField>

      <FormField
        label={passwordLabel}
        name="password"
        error={errors.password?.message}
      >
        <TextInput
          {...register("password", {
            required: t(keys.errors.required),
            onChange: () => {
              setGlobalError(null);
              clearLoginFailure();
              clearErrors(["phone", "password"]);
            },
          })}
          type={isPasswordVisible ? "text" : "password"}
          autoComplete="current-password"
          enterKeyHint="done"
          placeholder={t(keys.placeholders.password)}
          hasError={Boolean(errors.password)}
          suffix={
            <IconButton
              variant="ghost"
              size="xs"
              ariaLabel={passwordToggleAriaLabel}
              onClick={() => setIsPasswordVisible((previous) => !previous)}
              className="pointer-events-auto"
              icon={<PasswordVisibilityIcon shown={isPasswordVisible} />}
            />
          }
          suffixSpace={36}
        />
      </FormField>

      {showRememberMe ? (
        <Controller
          control={control}
          name="remember"
          render={({ field }) => (
            <Checkbox
              size="sm"
              checked={Boolean(field.value)}
              onChange={(event) => field.onChange(event.target.checked)}
              label={
                <span className="text-[12.5px] text-[color:var(--text-secondary)]">
                  {t(keys.auth.rememberMe)}
                </span>
              }
            />
          )}
        />
      ) : null}

      <Button
        type="submit"
        variant="secondary"
        size="lg"
        fullWidth
        isLoading={isSubmitting || isPending}
        trailingIcon={<ArrowRightIcon />}
      >
        {t(keys.auth.loginAction)}
      </Button>
    </form>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="14"
      height="14"
      aria-hidden="true"
      className="rtl:rotate-180"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function PasswordVisibilityIcon({ shown }: { shown: boolean }) {
  if (shown) {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="h-4 w-4"
        aria-hidden="true"
      >
        <path d="M3 3l18 18" strokeLinecap="round" strokeLinejoin="round" />
        <path
          d="M10.584 10.586a2 2 0 002.83 2.828"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.88 5.09A9.14 9.14 0 0112 4.75c4.478 0 8.268 2.943 9.53 7a10.522 10.522 0 01-3.146 4.57M6.228 6.228A10.45 10.45 0 002.47 11.75a10.523 10.523 0 005.35 6.1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path
        d="M2.47 11.75C3.732 7.693 7.522 4.75 12 4.75c4.478 0 8.268 2.943 9.53 7-1.262 4.057-5.052 7-9.53 7-4.478 0-8.268-2.943-9.53-7z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="11.75" r="3" />
    </svg>
  );
}
